import { toast } from 'react-toastify';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Settings as SettingsIcon, Trash2, Pencil, X } from 'lucide-react';
import { uploadImage, getImages } from '../services/uploadService';
import PhotoGallery from './PhotoGallery';
import ConfirmDialog from './ConfirmDialog';
import RichTextEditor from './RichTextEditor';
import SortableList from './SortableList';
import ShimmerLoader from './ShimmerLoader';
import AddItemsModal from './AddItemsModal';
import Pagination from 'react-responsive-pagination';
import AddModelsModal from './AddModelsModal';
import AddUnitsModal from './AddUnitsModal';
import { PackageModel } from '../types/packageModel';
import { PackageUnit } from '../types/packageUnit';
import { optionModelService } from '../services/optionModelService';
import { optionUnitService } from '../services/optionUnitService';
import api from '../services/api';
import '../styles/OptionDetailsSlider.css';

interface OptionDetailsSliderProps {
  workspaceId: string;
  optionId: string;
  onClose: () => void;
  onSave?: () => void;
}

interface Option {
  _id: string;
  name: string;
  caption?: string;
  description?: string;
  specifications?: string;
  projectId: string;
  projectName?: string;
  categoryId?: string;
  categoryName?: string;
  location?: string;
  phase?: string;
  models?: string;
  isActive: boolean;
  isValid: boolean;
  order: number;
  displayQuantity: boolean;
  heroImageId?: string;
  photoIds?: string[];
  spaces?: string[];
  trades?: string;
  constructionNotes?: string;
  onlySoldWithCollections?: CollectionReference[];
  incompatibleCollections?: CollectionReference[];
  onlySoldWithOptions?: OptionReference[];
  onlySoldWithPackages?: PackageReference[];
  incompatibleOptions?: OptionReference[];
  incompatiblePackages?: PackageReference[];
  createdAt: string;
  updatedAt: string;
}

interface CollectionReference {
  _id: string;
  name?: string;
  itemsUpgraded?: string[];
}

interface PackageReference {
  _id: string;
  name?: string;
}

interface OptionReference {
  _id: string;
  name?: string;
}

// Removed unused Project interface

interface Category {
  _id: string;
  name: string;
}

interface PackageLocation {
  _id: string;
  name: string;
  order: number;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface PurchasedUnit {
  unitNo: string;
  unitId: string;
  unitStatus: string;
  dealNumber: string;
  dealId: string;
  purchasers: Array<{ displayName: string; isPrimary: boolean; contactId: string }>;
  dealStage: string;
}

const OptionDetailsSlider: React.FC<OptionDetailsSliderProps> = ({
  workspaceId,
  optionId,
  onClose,
  onSave
}) => {
  const [option, setOption] = useState<Option | null>(null);
  const [loading, setLoading] = useState(true);
  // Removed unused error/projects state to eliminate lint warnings and unused variables
  // If needed later for UI error presentation, they can be reintroduced
  // const [error, setError] = useState('');
  // const [projects, setProjects] = useState<Project[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<PackageLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Hero image
  const [uploadingHero, setUploadingHero] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);

  // General info editing
  const [editingGeneralInfo, setEditingGeneralInfo] = useState(false);
  const [generalInfoData, setGeneralInfoData] = useState({
    name: '',
    caption: '',
    description: '',
    location: '',
    phase: '',
    models: '',
    isActive: true,
    isValid: true,
    order: 0,
    displayQuantity: true,
    categoryId: '',
    specifications: ''
  });
  const [savingGeneralInfo, setSavingGeneralInfo] = useState(false);

  // Description & Construction Notes editing
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionData, setDescriptionData] = useState({
    description: '',
    constructionNotes: ''
  });
  const [savingDescription, setSavingDescription] = useState(false);

  // Spaces editing
  const [editingSpaces, setEditingSpaces] = useState(false);
  const [spacesData, setSpacesData] = useState<string[]>([]);
  const [savingSpaces, setSavingSpaces] = useState(false);
  const [showAddSpacesModal, setShowAddSpacesModal] = useState(false);

  // Color Collections management
  const [colorCollections, setColorCollections] = useState<any[]>([]);
  const [loadingColorCollections, setLoadingColorCollections] = useState(false);
  const [showAddColorCollectionModal, setShowAddColorCollectionModal] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [showIncompatibleCollectionsModal, setShowIncompatibleCollectionsModal] = useState(false);
  const [selectedIncompatibleCollections, setSelectedIncompatibleCollections] = useState<string[]>([]);
  const [showItemsUpgradedModal, setShowItemsUpgradedModal] = useState(false);
  const [currentCollectionForItems, setCurrentCollectionForItems] = useState<any>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);

  // Models management
  const [optionModels, setOptionModels] = useState<PackageModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [showAddModelsModal, setShowAddModelsModal] = useState(false);
  const [showEditModelsModal, setShowEditModelsModal] = useState(false);
  // Removed unused editingModel state

  // Units management
  const [optionUnits, setOptionUnits] = useState<PackageUnit[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [showAddUnitsModal, setShowAddUnitsModal] = useState(false);
  const [showEditUnitsModal, setShowEditUnitsModal] = useState(false);
  // Removed unused editingUnit state

  // Relationship management
  // Removed unused showSoldWithCollectionsModal state (implementation uses direct conditional flags elsewhere)
  const [showSoldWithOptionsModal, setShowSoldWithOptionsModal] = useState(false);
  const [showSoldWithPackagesModal, setShowSoldWithPackagesModal] = useState(false);
  const [showIncompatibleOptionsModal, setShowIncompatibleOptionsModal] = useState(false);
  const [showIncompatiblePackagesModal, setShowIncompatiblePackagesModal] = useState(false);
  const [availableOptions, setAvailableOptions] = useState<any[]>([]);
  const [availablePackages, setAvailablePackages] = useState<any[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);

  // Purchased Units
  const [purchasedUnits, setPurchasedUnits] = useState<PurchasedUnit[]>([]);
  const [loadingPurchasedUnits, setLoadingPurchasedUnits] = useState(false);
  const [purchasedUnitsPagination, setPurchasedUnitsPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Tabs
  const [activeTab, setActiveTab] = useState<'general' | 'settings' | 'models' | 'units' | 'purchased'>('general');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Stable callbacks to satisfy hook dependency rules
  const fetchHeroImage = useCallback(async (imageId: string) => {
    try {
      const images = await getImages(workspaceId, {
        entityType: 'option',
        entityId: optionId,
        imageType: 'hero'
      });
      if (images.length > 0) {
        setHeroImageUrl(images[0].url);
      }
    } catch (err) {
      console.error('Error fetching hero image:', err);
    }
  }, [workspaceId, optionId]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/pkg-categories`, {
        params: { limit: 1000, sortBy: 'name', sortOrder: 'asc' }
      });
      setCategories(response.data.categories || []);
    } catch (err: any) {
      console.error('Error fetching categories:', err);
    }
  }, [workspaceId]);

  const fetchLocations = useCallback(async (projectId: string) => {
    if (!projectId) {
      setLocations([]);
      return;
    }
    try {
      setLoadingLocations(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkglocations`, {
        params: { projectId, limit: 1000, sortBy: 'order', sortOrder: 'asc' }
      });
      setLocations(response.data.locations || []);
    } catch (err: any) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, [workspaceId]);

  const fetchOption = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-options/${optionId}`);
      const data = response.data.option;
      setOption(data);
      setGeneralInfoData({
        name: data.name || '',
        caption: data.caption || '',
        description: data.description || '',
        location: data.location || '',
        phase: data.phase || '',
        models: data.models || '',
        isActive: data.isActive ?? true,
        isValid: data.isValid ?? true,
        order: data.order || 0,
        displayQuantity: data.displayQuantity ?? true,
        categoryId: data.categoryId || '',
        specifications: data.specifications || ''
      });
      setDescriptionData({
        description: data.description || '',
        constructionNotes: data.constructionNotes || ''
      });
      if (data.heroImageId) {
        fetchHeroImage(data.heroImageId);
      }
    } catch (err: any) {
      console.error('Error fetching option:', err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, optionId, fetchHeroImage]);

  const fetchColorCollections = useCallback(async () => {
    if (!option?.projectId) return;
    try {
      setLoadingColorCollections(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-color-collections`, {
        params: { projectId: option.projectId }
      });
      setColorCollections(response.data.colorCollections || []);
    } catch (err: any) {
      console.error('Error fetching color collections:', err);
    } finally {
      setLoadingColorCollections(false);
    }
  }, [workspaceId, option?.projectId]);

  const fetchOptionModels = useCallback(async () => {
    try {
      setLoadingModels(true);
      const response = await optionModelService.getByPackage(workspaceId, optionId);
      setOptionModels(response.models || []);
    } catch (err: any) {
      console.error('Error fetching option models:', err);
    } finally {
      setLoadingModels(false);
    }
  }, [workspaceId, optionId]);

  const fetchOptionUnits = useCallback(async () => {
    try {
      setLoadingUnits(true);
      const response = await optionUnitService.getByPackage(workspaceId, optionId);
      setOptionUnits(response.units || []);
    } catch (err: any) {
      console.error('Error fetching option units:', err);
    } finally {
      setLoadingUnits(false);
    }
  }, [workspaceId, optionId]);

  const fetchPurchasedUnits = async () => {
    try {
      setLoadingPurchasedUnits(true);
      const response = await api.get(
        `/workspaces/${workspaceId}/pkg-options/${optionId}/purchased-units`,
        {
          params: {
            page: purchasedUnitsPagination.page,
            limit: purchasedUnitsPagination.limit
          }
        }
      );
      setPurchasedUnits(response.data.purchasedUnits || []);
      if (response.data.pagination) {
        setPurchasedUnitsPagination(response.data.pagination);
      }
    } catch (err: any) {
      console.error('Error fetching purchased units:', err);
    } finally {
      setLoadingPurchasedUnits(false);
    }
  };

  const handlePurchasedUnitsPageChange = (page: number) => {
    setPurchasedUnitsPagination({ ...purchasedUnitsPagination, page });
  };

  const handlePurchasedUnitsLimitChange = (limit: number) => {
    setPurchasedUnitsPagination({ ...purchasedUnitsPagination, limit, page: 1 });
  };

  useEffect(() => {
    fetchCategories();
    fetchOption();
  }, [fetchCategories, fetchOption]);

  useEffect(() => {
    if (option?.projectId) {
      fetchLocations(option.projectId);
    }
  }, [option?.projectId, fetchLocations]);

  useEffect(() => {
    if (activeTab === 'settings' && option?.projectId) {
      fetchColorCollections();
    }
  }, [activeTab, option?.projectId, fetchColorCollections]);

  useEffect(() => {
    if (activeTab === 'models') {
      fetchOptionModels();
    }
  }, [activeTab, fetchOptionModels]);

  useEffect(() => {
    if (activeTab === 'units') {
      fetchOptionUnits();
    }
  }, [activeTab, fetchOptionUnits]);

  useEffect(() => {
    if (activeTab === 'purchased') {
      fetchPurchasedUnits();
    }
  }, [activeTab, purchasedUnitsPagination.page, purchasedUnitsPagination.limit]);

  // Removed duplicate non-callback fetch functions to avoid redeclarations and hook warnings

  const handleGeneralInfoChange = (field: string, value: any) => {
    setGeneralInfoData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveGeneralInfo = async () => {
    try {
      setSavingGeneralInfo(true);

      const { description, ...generalInfoWithoutDescription } = generalInfoData;

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        generalInfoWithoutDescription,
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, ...generalInfoWithoutDescription } : null);
      setEditingGeneralInfo(false);
      if (onSave) onSave();
    } catch (err: any) {
      console.error('Error saving option:', err);
      toast.error(err.response?.data?.message || 'Failed to save option');
    } finally {
      setSavingGeneralInfo(false);
    }
  };

  const handleDescriptionChange = (field: string, value: any) => {
    setDescriptionData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDescription = async () => {
    try {
      setSavingDescription(true);

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        descriptionData,
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, ...descriptionData } : null);
      setEditingDescription(false);
      if (onSave) onSave();
    } catch (err: any) {
      console.error('Error saving description:', err);
      toast.error(err.response?.data?.message || 'Failed to save description');
    } finally {
      setSavingDescription(false);
    }
  };

  // Spaces handlers
  const handleEditSpaces = () => {
    if (!option) return;
    setSpacesData(option.spaces || []);
    setEditingSpaces(true);
  };

  const handleCancelSpaces = () => {
    setEditingSpaces(false);
  };

  const handleSpacesChange = (spaces: string[]) => {
    setSpacesData(spaces);
  };

  const handleAddSpacesFromModal = (newSpaces: string[]) => {
    setSpacesData(prev => [...prev, ...newSpaces]);
  };

  const handleSaveSpaces = async () => {
    if (!option) return;

    try {
      setSavingSpaces(true);

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { spaces: spacesData },
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, spaces: spacesData } : null);
      setEditingSpaces(false);
      if (onSave) onSave();
    } catch (err: any) {
      console.error('Error saving spaces:', err);
      toast.error(err.response?.data?.message || 'Failed to save spaces');
    } finally {
      setSavingSpaces(false);
    }
  };

  // Color Collections handlers

  const handleToggleCollection = (collectionId: string) => {
    setSelectedCollections(prev =>
      prev.includes(collectionId)
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const handleAddSelectedCollections = async () => {
    if (selectedCollections.length === 0) {
      toast.warning('Please select at least one color collection');
      return;
    }

    try {
      const collectionsToAdd = selectedCollections.map(collectionId => {
        const collection = colorCollections.find(c => c._id === collectionId);
        return {
          _id: collection._id,
          name: collection.name
        };
      });

      const updatedCollections = [
        ...(option?.onlySoldWithCollections || []),
        ...collectionsToAdd
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { onlySoldWithCollections: updatedCollections },
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, onlySoldWithCollections: updatedCollections } : null);
      setSelectedCollections([]);
      setShowAddColorCollectionModal(false);
      toast.success(`${selectedCollections.length} color collection(s) added successfully`);
    } catch (err: any) {
      console.error('Error adding color collections:', err);
      toast.error(err.response?.data?.message || 'Failed to add color collections');
    }
  };

  const handleRemoveColorCollection = async (collectionId: string) => {
    try {
      const updatedCollections = (option?.onlySoldWithCollections || []).filter(
        c => c._id !== collectionId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { onlySoldWithCollections: updatedCollections },
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, onlySoldWithCollections: updatedCollections } : null);
    } catch (err: any) {
      console.error('Error removing color collection:', err);
      toast.error(err.response?.data?.message || 'Failed to remove color collection');
    }
  };

  // Items Upgraded handlers
  const handleOpenItemsUpgradedModal = async (collection: CollectionReference) => {
    try {
      // Fetch the full collection details with itemCategories
      const response = await api.get(`/workspaces/${workspaceId}/pkg-color-collections/${collection._id}`);
      setCurrentCollectionForItems(response.data.colorCollection || response.data);
      setSelectedItemIds(collection.itemsUpgraded || []);
      setShowItemsUpgradedModal(true);
    } catch (err: any) {
      console.error('Error fetching collection details:', err);
      toast.error('Failed to load collection details');
    }
  };

  const handleToggleItemUpgraded = (itemId: string) => {
    setSelectedItemIds(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSaveItemsUpgraded = async () => {
    try {
      if (!currentCollectionForItems) return;

      const updatedCollections = (option?.onlySoldWithCollections || []).map(col =>
        col._id === currentCollectionForItems._id
          ? { ...col, itemsUpgraded: selectedItemIds }
          : col
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { onlySoldWithCollections: updatedCollections },
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, onlySoldWithCollections: updatedCollections } : null);
      setShowItemsUpgradedModal(false);
      setCurrentCollectionForItems(null);
      setSelectedItemIds([]);
      toast.success('Items upgraded updated successfully');
    } catch (err: any) {
      console.error('Error updating items upgraded:', err);
      toast.error(err.response?.data?.message || 'Failed to update items upgraded');
    }
  };

  // Incompatible Collections handlers
  const handleToggleIncompatibleCollection = (collectionId: string) => {
    setSelectedIncompatibleCollections(prev =>
      prev.includes(collectionId)
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const handleAddSelectedIncompatibleCollections = async () => {
    if (selectedIncompatibleCollections.length === 0) {
      toast.warning('Please select at least one color collection');
      return;
    }

    try {
      const collectionsToAdd = selectedIncompatibleCollections.map(collectionId => {
        const collection = colorCollections.find(c => c._id === collectionId);
        return {
          _id: collection!._id,
          name: collection!.name
        };
      });

      const updatedCollections = [
        ...(option?.incompatibleCollections || []),
        ...collectionsToAdd
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { incompatibleCollections: updatedCollections },
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, incompatibleCollections: updatedCollections } : null);
      setSelectedIncompatibleCollections([]);
      setShowIncompatibleCollectionsModal(false);
      toast.success(`${selectedIncompatibleCollections.length} incompatible collection(s) added successfully`);
    } catch (err: any) {
      console.error('Error adding incompatible collections:', err);
      toast.error(err.response?.data?.message || 'Failed to add incompatible collections');
    }
  };

  const handleRemoveIncompatibleCollection = async (collectionId: string) => {
    try {
      const updatedCollections = (option?.incompatibleCollections || []).filter(
        c => c._id !== collectionId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { incompatibleCollections: updatedCollections },
      );

      // Update local state directly instead of fetching
      setOption(prev => prev ? { ...prev, incompatibleCollections: updatedCollections } : null);
    } catch (err: any) {
      console.error('Error removing incompatible collection:', err);
      toast.error(err.response?.data?.message || 'Failed to remove incompatible collection');
    }
  };

  // Models handlers

  const handleAddModels = async (models: Array<{ modelId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      await optionModelService.createMultiple(workspaceId, optionId, models);
      await fetchOptionModels();
      setShowAddModelsModal(false);
    } catch (err: any) {
      console.error('Error adding models:', err);
      toast.error(err.response?.data?.message || 'Failed to add models');
    }
  };

  const handleEditModels = async (models: Array<{ modelId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingModels(true);
      for (const model of models) {
        const existingModel = optionModels.find(m => m.modelId === model.modelId);
        if (existingModel) {
          await optionModelService.update(
            workspaceId,
            optionId,
            model.modelId,
            {
              isUpgrade: model.isUpgrade,
              cost: model.cost,
              price: model.price
            }
          );
        }
      }
      await fetchOptionModels();
      setShowEditModelsModal(false);
    } catch (err: any) {
      console.error('Error updating models:', err);
      toast.error(err.response?.data?.message || 'Failed to update models');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleDeleteModel = (modelId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Model',
      message: 'Are you sure you want to remove this model?',
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await optionModelService.delete(workspaceId, optionId, modelId);
          // Update local state directly
          setOptionModels(prev => prev.filter(m => m.modelId !== modelId));
        } catch (err: any) {
          console.error('Error deleting model:', err);
          toast.error(err.response?.data?.message || 'Failed to delete model');
        }
      }
    });
  };

  // Units handlers

  const handleAddUnits = async (units: Array<{ unitId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      await optionUnitService.createMultiple(workspaceId, optionId, units);
      await fetchOptionUnits();
      setShowAddUnitsModal(false);
    } catch (err: any) {
      console.error('Error adding units:', err);
      toast.error(err.response?.data?.message || 'Failed to add units');
    }
  };

  const handleEditUnits = async (units: Array<{ unitId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingUnits(true);
      for (const unit of units) {
        const existingUnit = optionUnits.find(u => u.unitId === unit.unitId);
        if (existingUnit) {
          await optionUnitService.update(
            workspaceId,
            optionId,
            unit.unitId,
            {
              isUpgrade: unit.isUpgrade,
              cost: unit.cost,
              price: unit.price
            }
          );
        }
      }
      await fetchOptionUnits();
      setShowEditUnitsModal(false);
    } catch (err: any) {
      console.error('Error updating units:', err);
      toast.error(err.response?.data?.message || 'Failed to update units');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleDeleteUnit = (unitId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Unit',
      message: 'Are you sure you want to remove this unit?',
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await optionUnitService.delete(workspaceId, optionId, unitId);
          // Update local state directly
          setOptionUnits(prev => prev.filter(u => u.unitId !== unitId));
        } catch (err: any) {
          console.error('Error deleting unit:', err);
          toast.error(err.response?.data?.message || 'Failed to delete unit');
        }
      }
    });
  };

  // Relationship handlers
  const fetchAvailableOptions = async () => {
    if (!option?.projectId) return;

    try {
      setLoadingOptions(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-options`, {
        params: { projectId: option.projectId, limit: 1000, sortBy: 'name', sortOrder: 'asc' }
      });
      setAvailableOptions(response.data.options.filter((o: any) => o._id !== optionId));
    } catch (err: any) {
      console.error('Error fetching options:', err);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchAvailablePackages = async () => {
    if (!option?.projectId) return;

    try {
      setLoadingPackages(true);
      const response = await api.get(`/workspaces/${workspaceId}/pkg-packages`, {
        params: { projectId: option.projectId, limit: 1000, sortBy: 'name', sortOrder: 'asc' }
      });
      setAvailablePackages(response.data.packages || []);
    } catch (err: any) {
      console.error('Error fetching packages:', err);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleAddSoldWithOption = async (optId: string) => {
    try {
      const selectedOption = availableOptions.find(o => o._id === optId);

      if (!selectedOption) return;

      const updatedOptions = [
        ...(option?.onlySoldWithOptions || []),
        {
          _id: selectedOption._id,
          name: selectedOption.name
        }
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { onlySoldWithOptions: updatedOptions },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, onlySoldWithOptions: updatedOptions } : null);
      setShowSoldWithOptionsModal(false);
    } catch (err: any) {
      console.error('Error adding sold with option:', err);
      toast.error(err.response?.data?.message || 'Failed to add option');
    }
  };

  const handleRemoveSoldWithOption = async (optId: string) => {
    try {
      const updatedOptions = (option?.onlySoldWithOptions || []).filter(
        o => o._id !== optId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { onlySoldWithOptions: updatedOptions },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, onlySoldWithOptions: updatedOptions } : null);
    } catch (err: any) {
      console.error('Error removing sold with option:', err);
      toast.error(err.response?.data?.message || 'Failed to remove option');
    }
  };

  const handleAddSoldWithPackage = async (pkgId: string) => {
    try {
      const selectedPackage = availablePackages.find(p => p._id === pkgId);

      if (!selectedPackage) return;

      const updatedPackages = [
        ...(option?.onlySoldWithPackages || []),
        {
          _id: selectedPackage._id,
          name: selectedPackage.name
        }
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { onlySoldWithPackages: updatedPackages },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, onlySoldWithPackages: updatedPackages } : null);
      setShowSoldWithPackagesModal(false);
    } catch (err: any) {
      console.error('Error adding sold with package:', err);
      toast.error(err.response?.data?.message || 'Failed to add package');
    }
  };

  const handleRemoveSoldWithPackage = async (pkgId: string) => {
    try {
      const updatedPackages = (option?.onlySoldWithPackages || []).filter(
        p => p._id !== pkgId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { onlySoldWithPackages: updatedPackages },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, onlySoldWithPackages: updatedPackages } : null);
    } catch (err: any) {
      console.error('Error removing sold with package:', err);
      toast.error(err.response?.data?.message || 'Failed to remove package');
    }
  };

  const handleAddIncompatibleOption = async (optId: string) => {
    try {
      const selectedOption = availableOptions.find(o => o._id === optId);

      if (!selectedOption) return;

      const updatedOptions = [
        ...(option?.incompatibleOptions || []),
        {
          _id: selectedOption._id,
          name: selectedOption.name
        }
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { incompatibleOptions: updatedOptions },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, incompatibleOptions: updatedOptions } : null);
      setShowIncompatibleOptionsModal(false);
    } catch (err: any) {
      console.error('Error adding incompatible option:', err);
      toast.error(err.response?.data?.message || 'Failed to add option');
    }
  };

  const handleRemoveIncompatibleOption = async (optId: string) => {
    try {
      const updatedOptions = (option?.incompatibleOptions || []).filter(
        o => o._id !== optId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { incompatibleOptions: updatedOptions },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, incompatibleOptions: updatedOptions } : null);
    } catch (err: any) {
      console.error('Error removing incompatible option:', err);
      toast.error(err.response?.data?.message || 'Failed to remove option');
    }
  };

  const handleAddIncompatiblePackage = async (pkgId: string) => {
    try {
      const selectedPackage = availablePackages.find(p => p._id === pkgId);

      if (!selectedPackage) return;

      const updatedPackages = [
        ...(option?.incompatiblePackages || []),
        {
          _id: selectedPackage._id,
          name: selectedPackage.name
        }
      ];

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { incompatiblePackages: updatedPackages },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, incompatiblePackages: updatedPackages } : null);
      setShowIncompatiblePackagesModal(false);
    } catch (err: any) {
      console.error('Error adding incompatible package:', err);
      toast.error(err.response?.data?.message || 'Failed to add package');
    }
  };

  const handleRemoveIncompatiblePackage = async (pkgId: string) => {
    try {
      const updatedPackages = (option?.incompatiblePackages || []).filter(
        p => p._id !== pkgId
      );

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { incompatiblePackages: updatedPackages },
      );

      // Update local state directly
      setOption(prev => prev ? { ...prev, incompatiblePackages: updatedPackages } : null);
    } catch (err: any) {
      console.error('Error removing incompatible package:', err);
      toast.error(err.response?.data?.message || 'Failed to remove package');
    }
  };

  const handleHeroImageClick = () => {
    heroInputRef.current?.click();
  };

  const handleHeroImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    try {
      setUploadingHero(true);
      const uploaded = await uploadImage({
        workspaceId,
        file,
        entityType: 'option',
        entityId: optionId,
        imageType: 'hero'
      });
      setHeroImageUrl(uploaded.url);

      await api.put(
        `/workspaces/${workspaceId}/pkg-options/${optionId}`,
        { heroImageId: uploaded._id },
      );
    } catch (err) {
      console.error('Error uploading hero image:', err);
    } finally {
      setUploadingHero(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Lock body scroll while the slider is open to prevent double scrollbars (body + inner content)
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (loading) {
    return (
      <div className="option-details-slider-overlay" onClick={handleClose}>
        <div className="option-details-slider" >
          <div className="option-details-header">
            <div className="option-details-title">
              <h2>Loading...</h2>
            </div>
            <button className="option-details-close-btn" onClick={handleClose}>
              <X size={24} />
            </button>
          </div>
          <div className="option-details-content">
            <ShimmerLoader type="details" />
          </div>
        </div>
      </div>
    );
  }

  const phases = [
    { name: 'One' },
    { name: 'Two' },
    { name: 'Three' },
    { name: 'Four' },
    { name: 'Five' }
  ];

  return (
    <div className="option-details-slider-overlay" onClick={handleClose}>
      <div className="option-details-slider" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="option-details-header">
          <div className="option-details-header-left">
            <button className="option-details-back-btn" onClick={handleClose}>
              ← Back
            </button>
            <div className="option-details-title">
              <h2>{option?.name || 'Option'}</h2>
              <p>{option?.projectName || 'Option Details'}</p>
            </div>
          </div>
          <button className="option-details-close-btn" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className="option-details-tabs">
          <button
            className={`option-details-tab ${activeTab === 'general' ? 'active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            General
          </button>
          <button
            className={`option-details-tab ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            Settings
          </button>
          <button
            className={`option-details-tab ${activeTab === 'models' ? 'active' : ''}`}
            onClick={() => setActiveTab('models')}
          >
            Models
          </button>
          <button
            className={`option-details-tab ${activeTab === 'units' ? 'active' : ''}`}
            onClick={() => setActiveTab('units')}
          >
            Units
          </button>
          <button
            className={`option-details-tab ${activeTab === 'purchased' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchased')}
          >
            Purchased Units
          </button>
        </div>

        {/* Content */}
        <div className="option-details-content">
          <div className="option-details-section">

            {/* General Tab */}
            {activeTab === 'general' && (
              <>
                {/* Grid Layout: Hero Image + Basic Information */}
                <div className="option-details-grid">
                  <div className="option-details-left">
                  {/* Hero Photo Tile - Left Side */}
                  <div className="option-details-tile">
                    <div className="option-details-tile-header">
                      <h3>Hero Image</h3>
                      <button
                        className="option-details-tile-edit"
                        onClick={handleHeroImageClick}
                        style={{ marginLeft: 'auto' }}
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                    <div className="option-details-tile-content">
                      <div
                        onClick={handleHeroImageClick}
                        style={{
                          border: '2px dashed #D1D5DB',
                          borderRadius: '8px',
                          padding: '32px',
                          textAlign: 'center',
                          backgroundColor: '#F9FAFB',
                          cursor: 'pointer',
                          minHeight: '300px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {uploadingHero ? (
                          <div className="builders-loading">
                            <div className="builders-loading-spinner" style={{ width: '48px', height: '48px' }} />
                            <p style={{ marginTop: '16px', fontSize: '14px', color: '#6B7280' }}>Uploading...</p>
                          </div>
                        ) : heroImageUrl ? (
                          <img
                            src={heroImageUrl}
                            alt="Hero"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '300px',
                              objectFit: 'contain',
                              borderRadius: '4px'
                            }}
                          />
                        ) : (
                          <>
                            <SettingsIcon size={48} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
                            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                              Drag & drop image here
                            </p>
                            <p style={{ fontSize: '12px', color: '#6B7280' }}>
                              or click to browse
                            </p>
                          </>
                        )}
                      </div>
                      <input
                        ref={heroInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleHeroImageChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  </div>
                  </div>

                  <div className="option-details-right">
                  {/* Basic Information Tile */}
                  <div className="option-details-tile">
                    <div className="option-details-tile-header">
                      <h3>Basic Information</h3>
                      {!editingGeneralInfo && (
                        <button
                          className="option-details-tile-edit"
                          onClick={() => setEditingGeneralInfo(true)}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                    <div className="option-details-tile-content">
                      {editingGeneralInfo ? (
                        <div className="option-details-form">
                          {/* Name and Order */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '12px' }}>
                            <div className="form-group">
                              <label>Name *</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.name}
                                onChange={(e) => handleGeneralInfoChange('name', e.target.value)}
                                placeholder="Enter option name"
                              />
                            </div>
                            <div className="form-group">
                              <label>Order</label>
                              <input
                                type="number"
                                className="form-input"
                                value={generalInfoData.order}
                                disabled
                                style={{ backgroundColor: '#F3F4F6', cursor: 'not-allowed' }}
                              />
                            </div>
                          </div>

                          {/* Active, Valid, Display Quantity */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', alignItems: 'end' }}>
                            <label className="drawer-form-checkbox-label" style={{ paddingBottom: '10px' }}>
                              <input
                                type="checkbox"
                                checked={generalInfoData.isActive}
                                onChange={(e) => handleGeneralInfoChange('isActive', e.target.checked)}
                              />
                              <span>Active</span>
                            </label>
                            <label className="drawer-form-checkbox-label" style={{ paddingBottom: '10px' }}>
                              <input
                                type="checkbox"
                                checked={generalInfoData.isValid}
                                onChange={(e) => handleGeneralInfoChange('isValid', e.target.checked)}
                              />
                              <span>Valid</span>
                            </label>
                            <label className="drawer-form-checkbox-label" style={{ paddingBottom: '10px' }}>
                              <input
                                type="checkbox"
                                checked={generalInfoData.displayQuantity}
                                onChange={(e) => handleGeneralInfoChange('displayQuantity', e.target.checked)}
                              />
                              <span>Display Quantity</span>
                            </label>
                          </div>

                          {/* Category, Locations and Phase */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label>Category</label>
                              <select
                                className="form-input"
                                value={generalInfoData.categoryId}
                                onChange={(e) => handleGeneralInfoChange('categoryId', e.target.value)}
                              >
                                <option value="">Select Category</option>
                                {categories.map(cat => (
                                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Locations *</label>
                              {loadingLocations ? (
                                <select className="form-input" disabled>
                                  <option>Loading locations...</option>
                                </select>
                              ) : (
                                <select
                                  className="form-input"
                                  value={generalInfoData.location}
                                  onChange={(e) => handleGeneralInfoChange('location', e.target.value)}
                                >
                                  <option value="">Select Location</option>
                                  {/* Show current value if it's not in the fetched locations */}
                                  {generalInfoData.location && !locations.find(loc => loc.name === generalInfoData.location) && (
                                    <option value={generalInfoData.location}>{generalInfoData.location}</option>
                                  )}
                                  {locations.map(loc => (
                                    <option key={loc._id} value={loc.name}>{loc.name}</option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <div className="form-group">
                              <label>Phase *</label>
                              <select
                                className="form-input"
                                value={generalInfoData.phase}
                                onChange={(e) => handleGeneralInfoChange('phase', e.target.value)}
                              >
                                <option value="">Select Phase</option>
                                {phases.map(phase => (
                                  <option key={phase.name} value={phase.name}>{phase.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Models and Caption */}
                          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div className="form-group">
                              <label>Models:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.models}
                                onChange={(e) => handleGeneralInfoChange('models', e.target.value)}
                                placeholder="Enter models"
                              />
                            </div>
                            <div className="form-group">
                              <label>Caption:</label>
                              <input
                                type="text"
                                className="form-input"
                                value={generalInfoData.caption}
                                onChange={(e) => handleGeneralInfoChange('caption', e.target.value)}
                                placeholder="Enter caption"
                              />
                            </div>
                          </div>

                          {/* Specifications */}
                          <div className="form-row" style={{ display: 'block' }}>
                            <div className="form-group">
                              <label>Specifications:</label>
                              <textarea
                                className="form-input"
                                value={generalInfoData.specifications}
                                onChange={(e) => handleGeneralInfoChange('specifications', e.target.value)}
                                placeholder="Type something"
                                rows={5}
                                style={{ width: '100%', resize: 'vertical' }}
                              />
                            </div>
                          </div>

                          <div className="option-details-form-actions">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setEditingGeneralInfo(false);
                                if (option) {
                                  setGeneralInfoData({
                                    name: option.name || '',
                                    caption: option.caption || '',
                                    description: option.description || '',
                                    location: option.location || '',
                                    phase: option.phase || '',
                                    models: option.models || '',
                                    isActive: option.isActive ?? true,
                                    isValid: option.isValid ?? true,
                                    order: option.order || 0,
                                    displayQuantity: option.displayQuantity ?? true,
                                    categoryId: option.categoryId || '',
                                    specifications: option.specifications || ''
                                  });
                                }
                              }}
                              disabled={savingGeneralInfo}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveGeneralInfo}
                              disabled={savingGeneralInfo || !generalInfoData.name}
                            >
                              {savingGeneralInfo ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="option-details-info">
                          {/* Row 1: Name and Order */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Name</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.name || '-'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '60px', fontWeight: 500, color: '#6B7280' }}>Order</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.order}</span>
                            </div>
                          </div>

                          {/* Row 2: Active, Valid, Display Quantity */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '60px', fontWeight: 500, color: '#6B7280' }}>Active</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.isActive ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '60px', fontWeight: 500, color: '#6B7280' }}>Valid</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.isValid ? 'Yes' : 'No'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '120px', fontWeight: 500, color: '#6B7280' }}>Display Quantity</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.displayQuantity ? 'Yes' : 'No'}</span>
                            </div>
                          </div>

                          {/* Row 3: Category and Location */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Category</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.categoryName || '-'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Location</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.location || '-'}</span>
                            </div>
                          </div>

                          {/* Row 4: Phase */}
                          <div style={{ marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Phase</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.phase || '-'}</span>
                            </div>
                          </div>

                          {/* Row 5: Models and Caption */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginBottom: '8px' }}>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Models</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.models || '-'}</span>
                            </div>
                            <div className="info-row" style={{ display: 'flex' }}>
                              <span className="info-label" style={{ minWidth: '100px', fontWeight: 500, color: '#6B7280' }}>Caption</span>
                              <span className="info-value" style={{ color: '#111827' }}>: {option?.caption || ''}</span>
                            </div>
                          </div>

                          {/* Row 7: Specifications */}
                          <div className="info-row" style={{ display: 'flex', marginBottom: '8px' }}>
                            <span className="info-label" style={{ minWidth: '140px', fontWeight: 500, color: '#6B7280' }}>Specifications</span>
                            <span className="info-value" style={{ color: '#111827' }}>: </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  </div>

                </div>

                {/* Description & Construction Notes and Spaces - Side by Side */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {/* Description & Construction Notes */}
                  <div className="option-details-tile">
                      <div className="option-details-tile-header">
                        <h3>Description & Construction Notes</h3>
                        {!editingDescription && (
                          <button
                            className="option-details-tile-edit"
                            onClick={() => setEditingDescription(true)}
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                      </div>
                      <div className="option-details-tile-content">
                        {editingDescription ? (
                          <div className="option-details-form">
                            <div className="form-row" style={{ display: 'block' }}>
                              <div className="form-group" style={{ width: '100%' }}>
                                <label>Description</label>
                                <RichTextEditor
                                  content={descriptionData.description}
                                  onChange={(content) => handleDescriptionChange('description', content)}
                                  placeholder="Enter description"
                                />
                              </div>
                            </div>

                            <div className="form-row" style={{ display: 'block' }}>
                              <div className="form-group" style={{ width: '100%' }}>
                                <label>Construction Notes</label>
                                <RichTextEditor
                                  content={descriptionData.constructionNotes}
                                  onChange={(content) => handleDescriptionChange('constructionNotes', content)}
                                  placeholder="Enter construction notes"
                                />
                              </div>
                            </div>

                            <div className="option-details-form-actions">
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => {
                                  setEditingDescription(false);
                                  if (option) {
                                    setDescriptionData({
                                      description: option.description || '',
                                      constructionNotes: option.constructionNotes || ''
                                    });
                                  }
                                }}
                                disabled={savingDescription}
                              >
                                Cancel
                              </button>
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={handleSaveDescription}
                                disabled={savingDescription}
                              >
                                {savingDescription ? 'Saving...' : 'Save'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="option-details-info">
                            <div style={{ marginBottom: '20px' }}>
                              <div className="info-row" style={{ marginBottom: '8px' }}>
                                <span className="info-label" style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Description</span>
                              </div>
                              <div className="info-row">
                                <div
                                  className="info-value"
                                  style={{ marginLeft: 0, color: '#6B7280', lineHeight: '1.6' }}
                                  dangerouslySetInnerHTML={{ __html: option?.description || 'No description provided' }}
                                />
                              </div>
                            </div>
                            <div>
                              <div className="info-row" style={{ marginBottom: '8px' }}>
                                <span className="info-label" style={{ fontWeight: 600, color: '#374151', fontSize: '14px' }}>Construction Notes</span>
                              </div>
                              <div className="info-row">
                                <div
                                  className="info-value"
                                  style={{ marginLeft: 0, color: '#6B7280', lineHeight: '1.6' }}
                                  dangerouslySetInnerHTML={{ __html: option?.constructionNotes || 'No construction notes provided' }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                  </div>

                  {/* Spaces */}
                  <div className="option-details-tile">
                    <div className="option-details-tile-header">
                      <h3>Spaces</h3>
                      {!editingSpaces && (
                        <button
                          className="option-details-tile-edit"
                          onClick={handleEditSpaces}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                    <div className="option-details-tile-content">
                      {editingSpaces ? (
                        <>
                          <div style={{ marginBottom: '16px' }}>
                            <SortableList
                              items={spacesData}
                              onChange={handleSpacesChange}
                              placeholder="Add custom space name"
                              addButtonText="+"
                            />
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => setShowAddSpacesModal(true)}
                              style={{ marginTop: '12px' }}
                            >
                              + Add from List
                            </button>
                          </div>
                          <div className="option-details-form-actions">
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={handleCancelSpaces}
                              disabled={savingSpaces}
                            >
                              Cancel
                            </button>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={handleSaveSpaces}
                              disabled={savingSpaces}
                            >
                              {savingSpaces ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="option-details-info">
                          {option?.spaces && option.spaces.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {option.spaces.map((space, index) => (
                                <span
                                  key={index}
                                  style={{
                                    padding: '4px 12px',
                                    backgroundColor: '#F3F4F6',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    color: '#374151'
                                  }}
                                >
                                  {space}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p style={{ color: '#6B7280', textAlign: 'center', padding: '20px' }}>
                              No spaces added yet
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Photo Gallery */}
                <div className="option-details-tile">
                    <div className="option-details-tile-header">
                      <h3>Photo Gallery</h3>
                    </div>
                    <div className="option-details-tile-content">
                      <PhotoGallery
                        workspaceId={workspaceId}
                        entityType="option"
                        entityId={optionId}
                        category="photo_gallery"
                        allowMultiple={true}
                      />
                    </div>
                </div>
              </>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <>
                {/* Only Sold With Color Collections Section */}
                <div className="option-details-tile">
                  <div className="option-details-tile-header">
                    <h3>Only Sold With Color Collections</h3>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchColorCollections();
                        setShowAddColorCollectionModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Collection
                    </button>
                  </div>
                  <div className="option-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: This option can only be sold if at least one of these color collections is selected.)
                    </p>
                    {option?.onlySoldWithCollections && option.onlySoldWithCollections.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Collection Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {option.onlySoldWithCollections.map((collection) => (
                              <tr key={collection._id}>
                                <td>{collection.name || collection._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: '#48182F',
                                        color: '#fff',
                                        padding: '4px 12px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px'
                                      }}
                                      onClick={() => handleOpenItemsUpgradedModal(collection)}
                                      title="Manage items upgraded"
                                    >
                                      Items Upgraded {collection.itemsUpgraded && collection.itemsUpgraded.length > 0 ? `(${collection.itemsUpgraded.length})` : ''}
                                    </button>
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: '#DC2626',
                                        color: '#fff',
                                        padding: '4px 8px'
                                      }}
                                      onClick={() => handleRemoveColorCollection(collection._id)}
                                      title="Remove collection"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No color collections added yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Incompatible Collections Section */}
                <div className="option-details-tile" style={{ marginTop: '20px' }}>
                  <div className="option-details-tile-header">
                    <h3>Incompatible Collections</h3>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchColorCollections();
                        setShowIncompatibleCollectionsModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Collection
                    </button>
                  </div>
                  <div className="option-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: This option is only available with selected Color Collections. User has to select one of the following collection in order to buy this option.)
                    </p>
                    {option?.incompatibleCollections && option.incompatibleCollections.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Collection Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {option.incompatibleCollections.map((collection) => (
                              <tr key={collection._id}>
                                <td>{collection.name || collection._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleRemoveIncompatibleCollection(collection._id)}
                                    title="Remove collection"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No incompatible collections added yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Only Sold With Options Section */}
                <div className="option-details-tile" style={{ marginTop: '20px' }}>
                  <div className="option-details-tile-header">
                    <h3>Only Sold With Options</h3>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchAvailableOptions();
                        setShowSoldWithOptionsModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Option
                    </button>
                  </div>
                  <div className="option-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: This option can only be sold if at least one of these options is also selected.)
                    </p>
                    {option?.onlySoldWithOptions && option.onlySoldWithOptions.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Option Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {option.onlySoldWithOptions.map((opt) => (
                              <tr key={opt._id}>
                                <td>{opt.name || opt._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleRemoveSoldWithOption(opt._id)}
                                    title="Remove option"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No options added yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Only Sold With Packages Section */}
                <div className="option-details-tile" style={{ marginTop: '20px' }}>
                  <div className="option-details-tile-header">
                    <h3>Only Sold With Packages</h3>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchAvailablePackages();
                        setShowSoldWithPackagesModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Package
                    </button>
                  </div>
                  <div className="option-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: This option can only be sold if at least one of these packages is selected.)
                    </p>
                    {option?.onlySoldWithPackages && option.onlySoldWithPackages.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Package Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {option.onlySoldWithPackages.map((pkg) => (
                              <tr key={pkg._id}>
                                <td>{pkg.name || pkg._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleRemoveSoldWithPackage(pkg._id)}
                                    title="Remove package"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No packages added yet
                      </div>
                    )}
                  </div>
                </div>

                {/* Incompatible Options Section */}
                <div className="option-details-tile" style={{ marginTop: '20px' }}>
                  <div className="option-details-tile-header">
                    <h3>Incompatible Options</h3>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchAvailableOptions();
                        setShowIncompatibleOptionsModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Option
                    </button>
                  </div>
                  <div className="option-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: If homeowner selects this option, the options listed here will not be available.)
                    </p>
                    {option?.incompatibleOptions && option.incompatibleOptions.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Option Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {option.incompatibleOptions.map((opt) => (
                              <tr key={opt._id}>
                                <td>{opt.name || opt._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleRemoveIncompatibleOption(opt._id)}
                                    title="Remove option"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No incompatible options found
                      </div>
                    )}
                  </div>
                </div>

                {/* Incompatible Packages Section */}
                <div className="option-details-tile" style={{ marginTop: '20px' }}>
                  <div className="option-details-tile-header">
                    <h3>Incompatible Packages</h3>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        fetchAvailablePackages();
                        setShowIncompatiblePackagesModal(true);
                      }}
                      style={{ marginLeft: 'auto' }}
                    >
                      + Add Package
                    </button>
                  </div>
                  <div className="option-details-tile-content">
                    <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px', fontStyle: 'italic' }}>
                      (Help: If homeowner selects this option, the packages listed here will not be available.)
                    </p>
                    {option?.incompatiblePackages && option.incompatiblePackages.length > 0 ? (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Package Name</th>
                              <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {option.incompatiblePackages.map((pkg) => (
                              <tr key={pkg._id}>
                                <td>{pkg.name || pkg._id}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleRemoveIncompatiblePackage(pkg._id)}
                                    title="Remove package"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                        No incompatible packages found
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Models Tab */}
            {activeTab === 'models' && (
              <div className="option-details-tile">
                <div className="option-details-tile-header">
                  <h3>Models</h3>
                  <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowEditModelsModal(true)}
                      disabled={optionModels.length === 0}
                    >
                      <Pencil size={16} style={{ marginRight: '4px' }} />
                      Edit Model
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAddModelsModal(true)}
                    >
                      + Add Model
                    </button>
                  </div>
                </div>
                <div className="option-details-tile-content">
                  {loadingModels ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      Loading models...
                    </div>
                  ) : optionModels && optionModels.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Model Collection</th>
                            <th>Model Name</th>
                            <th>Type</th>
                            <th>Upgrade</th>
                            <th>Cost</th>
                            <th>Price</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {optionModels.map((model) => (
                            <tr key={model._id}>
                              <td>{model.modelCollection || '-'}</td>
                              <td>{model.modelName || '-'}</td>
                              <td>{model.modelType || '-'}</td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  backgroundColor: model.isUpgrade ? '#DEF7EC' : '#FEE2E2',
                                  color: model.isUpgrade ? '#03543F' : '#991B1B'
                                }}>
                                  {model.isUpgrade ? 'Upgrade' : 'Standard'}
                                </span>
                              </td>
                              <td>${model.cost?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td>${model.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#DC2626',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    marginLeft: 'auto'
                                  }}
                                  onClick={() => handleDeleteModel(model.modelId)}
                                  title="Remove model"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      No models added yet. Click "Add Model" to get started.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Units Tab */}
            {activeTab === 'units' && (
              <div className="option-details-tile">
                <div className="option-details-tile-header">
                  <h3>Units</h3>
                  <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setShowEditUnitsModal(true)}
                      disabled={optionUnits.length === 0}
                    >
                      <Pencil size={16} style={{ marginRight: '4px' }} />
                      Edit Unit
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAddUnitsModal(true)}
                    >
                      + Add Unit
                    </button>
                  </div>
                </div>
                <div className="option-details-tile-content">
                  {loadingUnits ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      Loading units...
                    </div>
                  ) : optionUnits && optionUnits.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Unit No</th>
                            <th>Floor</th>
                            <th>Model</th>
                            <th>Status</th>
                            <th>Upgrade</th>
                            <th>Cost</th>
                            <th>Price</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {optionUnits.map((unit) => (
                            <tr key={unit._id}>
                              <td>{unit.unitNo || '-'}</td>
                              <td>{unit.floorMarketing || '-'}</td>
                              <td>{unit.modelName || '-'}</td>
                              <td>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor:
                                    unit.status === 'AVAILABLE' ? '#DEF7EC' :
                                    unit.status === 'RESERVED' ? '#FEF3C7' :
                                    unit.status === 'SOLD' ? '#FEE2E2' : '#F3F4F6',
                                  color:
                                    unit.status === 'AVAILABLE' ? '#03543F' :
                                    unit.status === 'RESERVED' ? '#92400E' :
                                    unit.status === 'SOLD' ? '#991B1B' : '#6B7280'
                                }}>
                                  {unit.status || '-'}
                                </span>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  backgroundColor: unit.isUpgrade ? '#DEF7EC' : '#FEE2E2',
                                  color: unit.isUpgrade ? '#03543F' : '#991B1B'
                                }}>
                                  {unit.isUpgrade ? 'Upgrade' : 'Standard'}
                                </span>
                              </td>
                              <td>${unit.cost?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td>${unit.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                              <td style={{ textAlign: 'right' }}>
                                <button
                                  className="btn btn-sm btn-ghost"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    background: '#DC2626',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    marginLeft: 'auto'
                                  }}
                                  onClick={() => handleDeleteUnit(unit.unitId)}
                                  title="Remove unit"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                      No units added yet. Click "Add Unit" to get started.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Purchased Units Tab */}
            {activeTab === 'purchased' && (
              <div className="option-details-tile">
                <div className="option-details-tile-content">
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Unit#</th>
                          <th>Unit Status</th>
                          <th>Deal#</th>
                          <th>Purchaser(s)</th>
                          <th>Deal Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingPurchasedUnits ? (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                              Loading purchased units...
                            </td>
                          </tr>
                        ) : purchasedUnits.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="data-table-empty">
                              No purchased units found for this option.
                            </td>
                          </tr>
                        ) : (
                          purchasedUnits.map((unit) => (
                            <tr key={unit.dealId}>
                              <td className="data-table-primary-cell">
                                <span
                                  onClick={() => window.open(`/workspaces/${workspaceId}/units?view=${unit.unitId}`, '_blank')}
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  title="View unit details"
                                >
                                  {unit.unitNo}
                                </span>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor: '#FEE2E2',
                                  color: '#991B1B'
                                }}>
                                  SOLD
                                </span>
                              </td>
                              <td>
                                <span
                                  onClick={() => window.open(`/workspaces/${workspaceId}/deals?view=${unit.dealId}`, '_blank')}
                                  style={{ cursor: 'pointer', textDecoration: 'underline', color: '#48182F' }}
                                  title="View deal details"
                                >
                                  {unit.dealNumber}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {unit.purchasers && unit.purchasers.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    {unit.purchasers.map((p, idx) => (
                                      <span
                                        key={idx}
                                        onClick={() => window.open(`/workspaces/${workspaceId}/contacts?view=${p.contactId}`, '_blank')}
                                        style={{
                                          fontWeight: p.isPrimary ? '600' : '400',
                                          cursor: 'pointer',
                                          textDecoration: 'underline'
                                        }}
                                        title="View purchaser details"
                                      >
                                        {p.displayName}
                                      </span>
                                    ))}
                                  </div>
                                ) : '-'}
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor:
                                    unit.dealStage === 'FIRM' ? '#DEF7EC' :
                                    unit.dealStage === 'RESERVED' ? '#FEF3C7' :
                                    unit.dealStage === 'UNIT RELEASED' ? '#DBEAFE' :
                                    unit.dealStage === 'SOLD' ? '#DEF7EC' : '#F3F4F6',
                                  color:
                                    unit.dealStage === 'FIRM' ? '#03543F' :
                                    unit.dealStage === 'RESERVED' ? '#92400E' :
                                    unit.dealStage === 'UNIT RELEASED' ? '#1E40AF' :
                                    unit.dealStage === 'SOLD' ? '#03543F' : '#6B7280'
                                }}>
                                  {unit.dealStage}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination and Records Per Page */}
                  {!loadingPurchasedUnits && purchasedUnits.length > 0 && (
                    <div className="contacts-pagination-controls" style={{ marginTop: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 8px' }}>
                      {/* Records Per Page Selector */}
                      <div className="contacts-records-per-page">
                        <span>Show</span>
                        <select
                          value={purchasedUnitsPagination.limit}
                          onChange={(e) => handlePurchasedUnitsLimitChange(Number(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span>records per page (Total: {purchasedUnitsPagination.total})</span>
                      </div>

                      {/* Pagination */}
                      {purchasedUnitsPagination.pages > 1 && (
                        <div className="contacts-pagination-wrapper">
                          <Pagination
                            current={purchasedUnitsPagination.page}
                            total={purchasedUnitsPagination.pages}
                            onPageChange={handlePurchasedUnitsPageChange}
                            maxWidth={400}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="option-details-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleClose}
          >
            Close
          </button>
        </div>

        {/* Add Spaces Modal */}
        <AddItemsModal
          isOpen={showAddSpacesModal}
          onClose={() => setShowAddSpacesModal(false)}
          onAdd={handleAddSpacesFromModal}
          availableOptions={[
            'Living Room',
            'Dining Room',
            'Kitchen',
            'Primary Bedroom',
            'Bedroom 2',
            'Bedroom 3',
            'Den',
            'Office',
            'Media Room',
            'Flex Room',
            'Powder Room',
            'Ensuite',
            'Main Bathroom',
            'Laundry Room',
            'Balcony',
            'Terrace',
            'Storage',
            'Foyer',
            'Hallway'
          ].filter(option => !spacesData.includes(option))}
          title="Add Spaces"
          customInputPlaceholder="Enter custom space name..."
        />

        {/* Add Color Collection Modal */}
        {showAddColorCollectionModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Color Collection</h3>
                <button className="modal-close-btn" onClick={() => {
                  setShowAddColorCollectionModal(false);
                  setSelectedCollections([]);
                }}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingColorCollections ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading collections...
                  </div>
                ) : (
                  <>
                    {selectedCollections.length > 0 && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#F5E6ED',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        fontSize: '13px',
                        color: '#48182F',
                        fontWeight: 500
                      }}>
                        {selectedCollections.length} collection(s) selected
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {colorCollections
                        .filter(collection =>
                          !option?.onlySoldWithCollections?.some(c => c._id === collection._id) &&
                          !option?.incompatibleCollections?.some(c => c._id === collection._id)
                        )
                        .map(collection => {
                          const isSelected = selectedCollections.includes(collection._id);
                          return (
                            <div
                              key={collection._id}
                              onClick={() => handleToggleCollection(collection._id)}
                              style={{
                                padding: '12px 16px',
                                border: `2px solid ${isSelected ? '#48182F' : '#E5E7EB'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                backgroundColor: isSelected ? '#F5E6ED' : '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer',
                                  accentColor: '#48182F'
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                  {collection.name}
                                </div>
                                {collection.caption && (
                                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                    {collection.caption}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {colorCollections.filter(collection =>
                        !option?.onlySoldWithCollections?.some(c => c._id === collection._id) &&
                        !option?.incompatibleCollections?.some(c => c._id === collection._id)
                      ).length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                          All available color collections have been added.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setShowAddColorCollectionModal(false);
                    setSelectedCollections([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  onClick={handleAddSelectedCollections}
                  disabled={selectedCollections.length === 0}
                >
                  Add Selected ({selectedCollections.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Incompatible Collections Modal */}
        {showIncompatibleCollectionsModal && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Incompatible Collection</h3>
                <button className="modal-close-btn" onClick={() => {
                  setShowIncompatibleCollectionsModal(false);
                  setSelectedIncompatibleCollections([]);
                }}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingColorCollections ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading collections...
                  </div>
                ) : (
                  <>
                    {selectedIncompatibleCollections.length > 0 && (
                      <div style={{
                        padding: '12px',
                        backgroundColor: '#F5E6ED',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        fontSize: '13px',
                        color: '#48182F',
                        fontWeight: 500
                      }}>
                        {selectedIncompatibleCollections.length} collection(s) selected
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {colorCollections
                        .filter(collection =>
                          !option?.incompatibleCollections?.some(c => c._id === collection._id) &&
                          !option?.onlySoldWithCollections?.some(c => c._id === collection._id)
                        )
                        .map(collection => {
                          const isSelected = selectedIncompatibleCollections.includes(collection._id);
                          return (
                            <div
                              key={collection._id}
                              onClick={() => handleToggleIncompatibleCollection(collection._id)}
                              style={{
                                padding: '12px 16px',
                                border: `2px solid ${isSelected ? '#48182F' : '#E5E7EB'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                backgroundColor: isSelected ? '#F5E6ED' : '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {}}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer',
                                  accentColor: '#48182F'
                                }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                  {collection.name}
                                </div>
                                {collection.caption && (
                                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                    {collection.caption}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {colorCollections.filter(collection =>
                        !option?.incompatibleCollections?.some(c => c._id === collection._id) &&
                        !option?.onlySoldWithCollections?.some(c => c._id === collection._id)
                      ).length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                          All available color collections have been added.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setShowIncompatibleCollectionsModal(false);
                    setSelectedIncompatibleCollections([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  onClick={handleAddSelectedIncompatibleCollections}
                  disabled={selectedIncompatibleCollections.length === 0}
                >
                  Add Selected ({selectedIncompatibleCollections.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Items Upgraded Modal */}
        {showItemsUpgradedModal && currentCollectionForItems && (
          <div className="modal-overlay">
            <div className="modal-content" style={{ maxWidth: '700px' }}>
              <div className="modal-header">
                <h3>Select Items Upgraded - {currentCollectionForItems.name}</h3>
                <button className="modal-close-btn" onClick={() => {
                  setShowItemsUpgradedModal(false);
                  setCurrentCollectionForItems(null);
                  setSelectedItemIds([]);
                }}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {selectedItemIds.length > 0 && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: '#F5E6ED',
                    borderRadius: '6px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#48182F',
                    fontWeight: 500
                  }}>
                    {selectedItemIds.length} item(s) selected
                  </div>
                )}

                {currentCollectionForItems.itemCategories && currentCollectionForItems.itemCategories.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {currentCollectionForItems.itemCategories.map((category: any) => (
                      <div key={category._id} style={{
                        border: '1px solid #E5E7EB',
                        borderRadius: '8px',
                        padding: '12px',
                        backgroundColor: '#F9FAFB'
                      }}>
                        <h4 style={{
                          fontSize: '15px',
                          fontWeight: 600,
                          color: '#48182F',
                          marginBottom: '12px',
                          paddingBottom: '8px',
                          borderBottom: '2px solid #48182F'
                        }}>
                          {category.name}
                        </h4>

                        {category.items && category.items.length > 0 ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {category.items.map((item: any) => {
                              const isSelected = selectedItemIds.includes(item._id);
                              return (
                                <div
                                  key={item._id}
                                  onClick={() => handleToggleItemUpgraded(item._id)}
                                  style={{
                                    padding: '10px 12px',
                                    border: `2px solid ${isSelected ? '#48182F' : '#D1D5DB'}`,
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    backgroundColor: isSelected ? '#F5E6ED' : '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '10px'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    style={{
                                      width: '18px',
                                      height: '18px',
                                      cursor: 'pointer',
                                      accentColor: '#48182F',
                                      marginTop: '2px',
                                      flexShrink: 0
                                    }}
                                  />
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                                      {item.name || 'Unnamed Item'}
                                    </div>
                                    {(item.location || item.type || item.selection) && (
                                      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                                        {[item.location, item.type, item.selection].filter(Boolean).join(' • ')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{
                            textAlign: 'center',
                            padding: '16px',
                            color: '#9CA3AF',
                            fontSize: '13px',
                            fontStyle: 'italic'
                          }}>
                            No items in this category
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    No item categories found in this collection
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => {
                    setShowItemsUpgradedModal(false);
                    setCurrentCollectionForItems(null);
                    setSelectedItemIds([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  onClick={handleSaveItemsUpgraded}
                >
                  Save ({selectedItemIds.length} items)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sold With Options Modal */}
        {showSoldWithOptionsModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Option (Only Sold With)</h3>
                <button className="modal-close-btn" onClick={() => setShowSoldWithOptionsModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingOptions ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading options...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availableOptions
                      .filter(o =>
                        !option?.onlySoldWithOptions?.some(so => so._id === o._id) &&
                        !option?.incompatibleOptions?.some(io => io._id === o._id)
                      )
                      .map(opt => (
                        <div
                          key={opt._id}
                          onClick={() => handleAddSoldWithOption(opt._id)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: '#FFFFFF'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5E6ED';
                            e.currentTarget.style.borderColor = '#48182F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                            {opt.name}
                          </div>
                          {opt.caption && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {opt.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    {availableOptions.filter(o =>
                      !option?.onlySoldWithOptions?.some(so => so._id === o._id) &&
                      !option?.incompatibleOptions?.some(io => io._id === o._id)
                    ).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        All available options have been added.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowSoldWithOptionsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sold With Packages Modal */}
        {showSoldWithPackagesModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Package (Only Sold With)</h3>
                <button className="modal-close-btn" onClick={() => setShowSoldWithPackagesModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingPackages ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading packages...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availablePackages
                      .filter(p =>
                        !option?.onlySoldWithPackages?.some(sp => sp._id === p._id) &&
                        !option?.incompatiblePackages?.some(ip => ip._id === p._id)
                      )
                      .map(pkg => (
                        <div
                          key={pkg._id}
                          onClick={() => handleAddSoldWithPackage(pkg._id)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: '#FFFFFF'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5E6ED';
                            e.currentTarget.style.borderColor = '#48182F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                            {pkg.name}
                          </div>
                          {pkg.caption && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {pkg.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    {availablePackages.filter(p =>
                      !option?.onlySoldWithPackages?.some(sp => sp._id === p._id) &&
                      !option?.incompatiblePackages?.some(ip => ip._id === p._id)
                    ).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        All available packages have been added.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowSoldWithPackagesModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Incompatible Options Modal */}
        {showIncompatibleOptionsModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Incompatible Option</h3>
                <button className="modal-close-btn" onClick={() => setShowIncompatibleOptionsModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingOptions ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading options...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availableOptions
                      .filter(o =>
                        !option?.incompatibleOptions?.some(io => io._id === o._id) &&
                        !option?.onlySoldWithOptions?.some(so => so._id === o._id)
                      )
                      .map(opt => (
                        <div
                          key={opt._id}
                          onClick={() => handleAddIncompatibleOption(opt._id)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: '#FFFFFF'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5E6ED';
                            e.currentTarget.style.borderColor = '#48182F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                            {opt.name}
                          </div>
                          {opt.caption && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {opt.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    {availableOptions.filter(o =>
                      !option?.incompatibleOptions?.some(io => io._id === o._id) &&
                      !option?.onlySoldWithOptions?.some(so => so._id === o._id)
                    ).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        All available options have been added.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowIncompatibleOptionsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Incompatible Packages Modal */}
        {showIncompatiblePackagesModal && (
          <div className="modal-overlay">
            <div className="modal-content"  style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Add Incompatible Package</h3>
                <button className="modal-close-btn" onClick={() => setShowIncompatiblePackagesModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {loadingPackages ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                    Loading packages...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availablePackages
                      .filter(p =>
                        !option?.incompatiblePackages?.some(ip => ip._id === p._id) &&
                        !option?.onlySoldWithPackages?.some(sp => sp._id === p._id)
                      )
                      .map(pkg => (
                        <div
                          key={pkg._id}
                          onClick={() => handleAddIncompatiblePackage(pkg._id)}
                          style={{
                            padding: '12px 16px',
                            border: '1px solid #E5E7EB',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            backgroundColor: '#FFFFFF'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#F5E6ED';
                            e.currentTarget.style.borderColor = '#48182F';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.borderColor = '#E5E7EB';
                          }}
                        >
                          <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                            {pkg.name}
                          </div>
                          {pkg.caption && (
                            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                              {pkg.caption}
                            </div>
                          )}
                        </div>
                      ))}
                    {availablePackages.filter(p =>
                      !option?.incompatiblePackages?.some(ip => ip._id === p._id) &&
                      !option?.onlySoldWithPackages?.some(sp => sp._id === p._id)
                    ).length === 0 && (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        All available packages have been added.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-md"
                  onClick={() => setShowIncompatiblePackagesModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Models Modal */}
        <AddModelsModal
          isOpen={showAddModelsModal}
          workspaceId={workspaceId}
          projectId={option?.projectId || ''}
          onClose={() => setShowAddModelsModal(false)}
          onSave={handleAddModels}
          existingModels={optionModels.map(m => ({
            _id: m._id,
            colorCollectionId: optionId,
            modelId: m.modelId,
            modelName: m.modelName,
            modelCollection: m.modelCollection,
            modelType: m.modelType,
            isUpgrade: m.isUpgrade,
            cost: m.cost,
            price: m.price,
            projectId: option?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
        />

        {/* Edit Models Modal */}
        <AddModelsModal
          isOpen={showEditModelsModal}
          workspaceId={workspaceId}
          projectId={option?.projectId || ''}
          onClose={() => setShowEditModelsModal(false)}
          onSave={handleEditModels}
          existingModels={optionModels.map(m => ({
            _id: m._id,
            colorCollectionId: optionId,
            modelId: m.modelId,
            modelName: m.modelName,
            modelCollection: m.modelCollection,
            modelType: m.modelType,
            isUpgrade: m.isUpgrade,
            cost: m.cost,
            price: m.price,
            projectId: option?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
          editMode={true}
        />

        {/* Add Units Modal */}
        <AddUnitsModal
          isOpen={showAddUnitsModal}
          workspaceId={workspaceId}
          projectId={option?.projectId || ''}
          onClose={() => setShowAddUnitsModal(false)}
          onSave={handleAddUnits}
          existingUnits={optionUnits.map(u => ({
            _id: u._id,
            colorCollectionId: optionId,
            unitId: u.unitId,
            unitNo: u.unitNo,
            floorMarketing: u.floorMarketing,
            modelName: u.modelName,
            status: u.status,
            isUpgrade: u.isUpgrade,
            cost: u.cost,
            price: u.price,
            projectId: option?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
        />

        {/* Edit Units Modal */}
        <AddUnitsModal
          isOpen={showEditUnitsModal}
          workspaceId={workspaceId}
          projectId={option?.projectId || ''}
          onClose={() => setShowEditUnitsModal(false)}
          onSave={handleEditUnits}
          existingUnits={optionUnits.map(u => ({
            _id: u._id,
            colorCollectionId: optionId,
            unitId: u.unitId,
            unitNo: u.unitNo,
            floorMarketing: u.floorMarketing,
            modelName: u.modelName,
            status: u.status,
            isUpgrade: u.isUpgrade,
            cost: u.cost,
            price: u.price,
            projectId: option?.projectId || '',
            workspaceId: workspaceId,
            createdBy: '',
            updatedBy: ''
          }))}
          editMode={true}
        />

        {/* General Confirm Dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
          onConfirm={confirmDialog.onConfirm}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonStyle="danger"
        />
      </div>
    </div>
  );
};

export default OptionDetailsSlider;
